/**
 * NauticalMark — six brand marks (Compass, Anchor, Lighthouse, Wheel, Sextant, Ship)
 *
 * Spec from Claude Design handoff (May 6):
 *   24×24 viewBox · 1.4px stroke · square caps · miter joins · currentColor
 *   Min 48px. Recommended 64–96px. Hero 128–256px.
 *
 * Color is inherited from the parent via CSS `color`. Three approved treatments:
 *   - Light surface: navy stroke (#0A1428 on #FFFFFF)
 *   - Dark surface: gold stroke (#D4AF37 on #040A14)
 *   - Cream surface: navy stroke (#0A1428 on #FFEEB6)
 *
 * Use ONLY for brand moments (login hero, empty states, loading splash,
 * email headers, marketing surfaces). Never inside CRM chrome (sidebar,
 * tables, buttons, form fields) — utility lucide icons are the language
 * for those.
 */
import React from 'react';

const MARKS = {
  compass: `
    <circle cx="12" cy="12" r="9.2"/>
    <circle cx="12" cy="12" r="6.4"/>
    <circle cx="12" cy="12" r="0.9" fill="currentColor"/>
    <path d="M12 2.8 13.6 11l-1.6 8.4-1.6-8.4z"/>
    <path d="M21.2 12 13 13.6l-8.4-1.6L13 10.4z"/>
    <path d="M12 4.5v1.8M12 17.7v1.8M4.5 12h1.8M17.7 12h1.8"/>
    <path d="m6.6 6.6 1.3 1.3M16.1 16.1l1.3 1.3M17.4 6.6l-1.3 1.3M7.9 16.1l-1.3 1.3"/>`,
  anchor: `
    <circle cx="12" cy="4.5" r="1.6"/>
    <path d="M12 6.1V21"/>
    <path d="M8 9h8"/>
    <path d="M4 14c0 4.4 3.6 7 8 7s8-2.6 8-7"/>
    <path d="M4 14h2.4M21.6 14H19.6"/>
    <path d="M4 14l1.4-1.4M20 14l-1.4-1.4"/>`,
  lighthouse: `
    <path d="M9.5 21h5"/>
    <path d="M10 21 11 11h2l1 10"/>
    <path d="M9.5 11h5"/>
    <rect x="10" y="7.5" width="4" height="3.5"/>
    <path d="M11 7.5V5.5h2v2"/>
    <path d="M12 5.5V3.2"/>
    <path d="M11.2 3.2h1.6"/>
    <path d="M5 9.5h2M17 9.5h2M5.5 7l1.4 1M18.5 7l-1.4 1"/>
    <path d="M7 21h10"/>
    <path d="M5 21l1.5-2.5h11L19 21"/>`,
  wheel: `
    <circle cx="12" cy="12" r="5"/>
    <circle cx="12" cy="12" r="1.4" fill="currentColor"/>
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4"/>
    <path d="m5.6 5.6 2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"/>`,
  sextant: `
    <path d="M3 19h18"/>
    <path d="M3 19A18 18 0 0 1 21 19"/>
    <path d="M5.6 19A14 14 0 0 1 18.4 19"/>
    <path d="M12 19V5.5"/>
    <path d="M12 5.5 19 19"/>
    <circle cx="12" cy="5.5" r="1"/>
    <path d="M9 19v-2M12 19v-2M15 19v-2M18 19v-2"/>`,
  ship: `
    <path d="M3 18.5c1.5 1.2 3 1.5 4.5 1.5s3-.3 4.5-1.5c1.5 1.2 3 1.5 4.5 1.5s3-.3 4.5-1.5"/>
    <path d="M4.5 16.5 6 13h12l1.5 3.5"/>
    <path d="M6 13V9.5h12V13"/>
    <path d="M12 9.5V3"/>
    <path d="M12 4.5 17 7.5 12 8.5z"/>
    <path d="M12 9 7 11 12 11.7z"/>`,
};

export const NauticalMark = ({ name, size = 64, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="square"
    strokeLinejoin="miter"
    aria-hidden="true"
    {...rest}
    dangerouslySetInnerHTML={{ __html: MARKS[name] }}
  />
);

export default NauticalMark;
